// Packages Import
import React, { memo, useState, useEffect } from "react";
import { Box, Grid, Button, Typography, FormControl, Checkbox, FormControlLabel } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
// Project Component
import DropdownFilter from "../../UI/DropdownFilter";
import AutocompleteDropdown from "../../UI/AutocompleteDropdown";
import ColoredTextField from "../../UI/ColoredTextField";
import Datepicker from "../../UI/Datepicker";
import { Code4ReportAction } from "../../../store/Code4Report/Code4Report-slice";
import { useSearchParams } from "react-router-dom";

const filterSettingData = ["Date Added", "Date Completed"];

const decodeParams = (encoded) => {
	try {
		const decoded = atob(encoded); // base64 decode
		const params = new URLSearchParams(decoded);
		return Object.fromEntries(params.entries());
	} catch (e) {
		console.error("Failed to decode:", e);
		return {};
	}
};

const SearchFilterArea = (props) => {
	const dispatch = useDispatch();
	const [refresh, setRefresh] = useState(false);
	const projects = useSelector((state) => state.code4ReportState.PROJECT_LIST_FILTER);
	const filteredData = useSelector((state) => state.code4ReportState.filteredData);
	const orgList = useSelector((state) => state.code4ReportState.orgList);
	const initialPageLoad = useSelector((state) => state.code4ReportState.initialPageLoad);
	const projectSelected = filteredData.filterOnProject ? { label: filteredData.filterOnProject, id: "" } : { label: "", id: "" };
	const startDate = moment(filteredData.startDate, "YYYY-MM-DD").isValid() ? filteredData.startDate : "";
	const endDate = moment(filteredData.endDate, "YYYY-MM-DD").isValid() ? filteredData.endDate : "";
	const userInfo = { org: useSelector((state) => state.authenticationState.User_Assigned_Orgs) };
	const accessToken = useSelector((state) => state.authenticationState.accessToken);

	const [searchParams, setSearchParams] = useSearchParams();
	const searchParamsData = searchParams.get("data") ? decodeParams(searchParams.get("data")) : undefined;
	const searchParamsBomPPN = searchParamsData ? searchParamsData["bom"] : undefined;
	const searchParamsProject = searchParamsData ? searchParamsData["project"] : undefined;
	const searchParamsUserOrg = searchParamsData ? searchParamsData["userOrg"] : undefined;

	const handleSelection = (val, type) => {
		switch (type) {
			case "name":
				const value = val !== null && val.label ? val.label : "";
				dispatch(Code4ReportAction.updateFilteredData({ data: { filterOnProject: value } }));
				if (filteredData.filterOnBOM && value !== "") {
					dispatch(Code4ReportAction.updateFilteredData({ data: { filterOnBOM: "" } }));
				}
				if (val === null) {
					dispatch(Code4ReportAction.updateFilteredData({ data: { all_entries: false } }));
				}
				break;
			case "setting":
				dispatch(Code4ReportAction.updateFilteredData({ data: { filterOn: val } }));
				break;
			case "start":
				dispatch(Code4ReportAction.updateFilteredData({ data: { startDate: moment(val).format("YYYY-MM-DD") } }));
				break;
			case "end":
				dispatch(Code4ReportAction.updateFilteredData({ data: { endDate: moment(val).format("YYYY-MM-DD") } }));
				break;
			case "org":
				dispatch(Code4ReportAction.updateFilteredData({ data: { userOrg: val } }));
				if (typeof props.getProjectsList === "function") {
					props.getProjectsList(val, "filter");
				}
				break;
			case "all":
				dispatch(Code4ReportAction.updateFilteredData({ data: val }));
				if (typeof props.getProjectsList === "function") {
					if ("userOrg" in val) {
						props.getProjectsList(val.userOrg, "filter");
					}
				}
				break;
			case "allEntries":
				dispatch(Code4ReportAction.updateFilteredData({ data: { all_entries: val } }));
				break;
			case "from_email":
				dispatch(Code4ReportAction.updateFilteredData({ data: { from_email: val } }));
				break;
			default:
				break;
		}
	};
	const handleBom = (event) => {
		dispatch(Code4ReportAction.updateFilteredData({ data: { filterOnBOM: event.target.value, all_entries: false } }));
		if (projectSelected.label) {
			dispatch(Code4ReportAction.updateFilteredData({ data: { filterOnProject: "" } }));
		}
	};

	const handleResetClick = () => {
		props.unblockRefFromEmail.current();

		handleSelection(
			{
				userOrg: userInfo.org.primaryOrg,
				filterOn: filterSettingData[0],
				startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
				endDate: moment().format("YYYY-MM-DD"),
				filterOnProject: "",
				filterOnBOM: "",
				all_entries: false,
				from_email: false,
			},
			"all"
		);
		setRefresh(true);
		setSearchParams({});
	};

	const handleApplyFilterClick = (initialPageLoad) => {
		if (typeof props.handleSearchFilterData === "function") {
			props.handleSearchFilterData(initialPageLoad);
			dispatch(Code4ReportAction.setSelectedRow([]));
		}
	};

	useEffect(() => {
		if (searchParamsBomPPN) {
			console.log("SearchParams", searchParamsData);
			handleSelection(
				{
					userOrg: searchParamsUserOrg ?? userInfo.org.primaryOrg,
					filterOn: filterSettingData[0],
					startDate: moment().subtract(1, "month").format("YYYY-MM-DD"),
					endDate: moment().format("YYYY-MM-DD"),
					filterOnProject: searchParamsProject ?? "",
					filterOnBOM: searchParamsBomPPN ?? "",
					all_entries: false,
					from_email: true,
				},
				"all"
			);
			props.unblockRefFromEmail.current();
		} else {
			const filterOnProject = sessionStorage.getItem("code4SearchFilterArea.filterOnProject");
			const filterOnBOM = sessionStorage.getItem("code4SearchFilterArea.filterOnBOM");
			handleSelection(
				{
					userOrg: sessionStorage.getItem("code4SearchFilterArea.userOrg") ?? userInfo.org.primaryOrg,
					filterOn: sessionStorage.getItem("code4SearchFilterArea.filterOn") ?? filterSettingData[0],
					startDate: sessionStorage.getItem("code4SearchFilterArea.startDate") ?? moment().subtract(1, "month").format("YYYY-MM-DD"),
					endDate: sessionStorage.getItem("code4SearchFilterArea.endDate") ?? moment().format("YYYY-MM-DD"),
					filterOnProject: filterOnProject ?? "",
					filterOnBOM: filterOnBOM && !filterOnProject ? filterOnProject : "",
					all_entries: sessionStorage.getItem("code4SearchFilterArea.all_entries") === "true" ?? false,
					from_email: false,
				},
				"all"
			);
		}
		dispatch(Code4ReportAction.setPageLoadState(true));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (initialPageLoad) {
			handleApplyFilterClick(true); // Mark this as first load
			if (filteredData.from_email) {
				props.fetchCode4Parts(filteredData.filterOnBOM);
				dispatch(Code4ReportAction.setFetchedbom_ppn(filteredData.filterOnBOM));
			}
			dispatch(Code4ReportAction.setPageLoadState(false));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialPageLoad]);

	useEffect(() => {
		const data = window.localStorage.getItem("code4ReportData");
		if (data && accessToken) {
			console.log("/Code4Report?data=" + data);

			window.localStorage.removeItem("code4ReportData");
			window.location.href = "/Code4Report?data=" + data;
		}
	}, [accessToken]);

	useEffect(() => {
		if (refresh) {
			handleApplyFilterClick();
			setRefresh(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refresh]);
	return (
		<Box>
			<Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={1} wrap="wrap" sx={{ mb: 1.5 }}>
				<Grid item xl={0.8} lg={0.9} md={1.5} sm={6} xs={12} textAlign="center">
					<DropdownFilter
						selections={orgList}
						handleSelection={handleSelection}
						label="Org"
						name="org"
						selectedData={filteredData.userOrg}
						highlightItemList={userInfo.org.primaryOrg}
						highlightColor="green"
						disabled={filteredData.from_email}
					/>
				</Grid>

				<Grid item xl={1.6} lg={1.6} md={2.5} sm={6} xs={12} textAlign="center">
					<DropdownFilter
						label="Filter Setting"
						selections={filterSettingData}
						handleSelection={handleSelection}
						name="setting"
						selectedData={filteredData.filterOn}
						disabled={filteredData.all_entries || filteredData.from_email}
					/>
				</Grid>
				<Grid item xl={1.35} lg={1.3} md={4} sm={6} xs={12} textAlign="center">
					<Datepicker
						label="Start Date"
						handleSelection={handleSelection}
						val={startDate}
						name="start"
						disableFuture={true}
						preventCut={true}
						disabled={filteredData.all_entries || filteredData.from_email}
					/>
				</Grid>
				<Grid item xl={1.35} lg={1.3} md={4} sm={6} xs={12} textAlign="center">
					<Datepicker
						label="End Date"
						handleSelection={handleSelection}
						name="end"
						val={endDate}
						disableFuture={true}
						preventCut={true}
						disabled={filteredData.all_entries || filteredData.from_email}
					/>
				</Grid>
				<Grid item xl={5.4} lg={5.2} md={12} sm={12} xs={12} textAlign="center">
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<FormControl size="small" sx={{ width: "230px" }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={filteredData.all_entries}
										onChange={(e) => handleSelection(e.target.checked, "allEntries")}
										disabled={!projectSelected.label || filteredData.from_email}
									/>
								}
								label="All Entries"
								labelPlacement="top"
								sx={{
									"& .MuiFormControlLabel-label": {
										fontSize: "11px",
									},
								}}
							/>
						</FormControl>
						<AutocompleteDropdown
							label="Project Name"
							selections={projects}
							value={filteredData.from_email ? "" : projectSelected}
							handleSelection={handleSelection}
							freeSolo={true}
							name="name"
							disabled={filteredData.from_email}
						/>
						<Typography variant="caption">OR.</Typography>
						<ColoredTextField
							label="Last Bom reviewed Search"
							fullWidth
							focused
							onChange={handleBom}
							value={filteredData.from_email ? "" : filteredData.filterOnBOM}
							InputLabelProps={{
								shrink: true,
							}}
							disabled={filteredData.from_email}
						/>
					</Box>
				</Grid>
				<Grid item xl={1.5} lg={1.7} md={12} sm={3} xs={12} textAlign="center">
					<Box>
						<Button variant="contained" onClick={handleApplyFilterClick} disabled={filteredData.from_email}>
							Apply
						</Button>
						<Button variant="contained" onClick={handleResetClick}>
							Reset
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
};

export default memo(SearchFilterArea);
